import React, { useEffect, useRef } from "react";
import { easeInOutExpo, easeOutElastic } from "./EasingFunctions";
import {
  drawGameZone,
  drawNoMatchesScale,
  drawProScale,
  drawScale,
} from "./DrawingFunctions";
import {
  countDecimals,
  createScaleConfig,
  linearMapping,
  mapRange,
} from "./UtilityFunctions";
import { WTNScaleCanvasProps } from "./WTNScaleCanvas.types";

declare global {
  interface Window {
    requestAnimFrame: Function;
  }
}

const WTNScale: React.FC<WTNScaleCanvasProps> = ({
  WTN,
  noWTNSingles,
  noWTNDoubles,
  gameZoneLower,
  gameZoneUpper,
  canvasWidth,
  animDuration,
  labels,
  version,
  proPlayer,
  confidence,
  sourceIsPsq,
  showTwoDecimalPoints,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const canvas: HTMLCanvasElement = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: false });

    if (ctx) {
      if (proPlayer) {
        const scaleSettings = createScaleConfig({
          canvas,
          canvasWidth,
          WTN: 1,
          labels,
          version,
          confidence,
          sourceIsPsq,
          showTwoDecimalPoints,
        });
        drawProScale({ ctx, config: scaleSettings });
      } else if (!proPlayer && WTN) {
        // create scale setting object
        const scaleSettings = createScaleConfig({
          canvas,
          canvasWidth,
          WTN,
          labels,
          version,
          confidence,
          sourceIsPsq,
          showTwoDecimalPoints,
        });
        const start = performance.now();
        const step = (time: number) => {
          // timeFraction goes from 0 to 1
          let timeFraction = (time - start) / animDuration;
          if (timeFraction > 1) timeFraction = 1;

          const progress1 = easeInOutExpo(timeFraction + 0.2);

          const currentAngle = linearMapping({
            progress: progress1,
            f0: scaleSettings.startingAngle,
            f1: mapRange({
              value: scaleSettings.score,
              low1: 40,
              high1: 1,
              low2: scaleSettings.startingAngle,
              high2: 2 * Math.PI + scaleSettings.staticDrawingFinishAngle,
            }),
          });

          if (WTN > 10 && gameZoneLower && gameZoneUpper) {
            if (timeFraction < 0.8) {
              drawScale({ ctx, angle: currentAngle, config: scaleSettings });
            } else if (timeFraction >= 0.8 && timeFraction <= 1) {
              drawScale({ ctx, angle: currentAngle, config: scaleSettings });

              const GZ_timeFranction =
                (time - start) / (0.2 * animDuration) - 4;

              const GZ_lineWidth = linearMapping({
                  progress: easeOutElastic(GZ_timeFranction),
                  f0: 0,
                  f1: (2 / 2.5) * scaleSettings.lineWidth,
                }),
                GZ_WTN_Upper = linearMapping({
                  progress: easeInOutExpo(GZ_timeFranction),
                  f0: 0,
                  f1:
                    countDecimals(gameZoneUpper) === 1
                      ? gameZoneUpper + 0.01
                      : gameZoneUpper,
                }), //Added correction of 0.01 when one decimal is received from the service, this will be truncated later.
                GZ_WTN_Lower = linearMapping({
                  progress: easeInOutExpo(GZ_timeFranction),
                  f0: 0,
                  f1:
                    countDecimals(gameZoneLower) === 1
                      ? gameZoneLower + 0.01
                      : gameZoneLower,
                });

              drawGameZone({
                ctx,
                x: scaleSettings.baseUnit / 2,
                y: scaleSettings.baseUnit / 2,
                radius:
                  scaleSettings.radius + (1 / 10) * scaleSettings.lineWidth,
                lineWidth: GZ_lineWidth,
                bgColor: scaleSettings.primaryColor,
                canvasWidth: scaleSettings.baseUnit,
                gameZoneFontSize: scaleSettings.gameZoneFontSize,
                WTNlowAnimated: Number.isInteger(gameZoneLower)
                  ? Math.round((GZ_WTN_Lower * 10) / 10)
                  : GZ_WTN_Lower, //When gameZone is integer we need to round to 1 decimal
                WTNHighAnimated: Number.isInteger(gameZoneUpper)
                  ? Math.round((GZ_WTN_Upper * 10) / 10)
                  : GZ_WTN_Upper,
                WTNlowStatic: gameZoneLower,
                WTNHighStatic: gameZoneUpper,
              });
            }
          } else {
            drawScale({ ctx, angle: currentAngle, config: scaleSettings });
          }

          if (timeFraction < 1) {
            window.requestAnimationFrame(step);
          }
        };
        window.requestAnimationFrame(step);
      } else if (!WTN) {
        const scaleSettings = createScaleConfig({
          canvas,
          canvasWidth,
          WTN: 40,
          labels,
          version,
          confidence,
          sourceIsPsq,
          showTwoDecimalPoints,
        });
        if (noWTNSingles) {
          drawNoMatchesScale({ ctx, config: scaleSettings, mode: "noSingles" });
        }
        if (noWTNDoubles) {
          drawNoMatchesScale({ ctx, config: scaleSettings, mode: "noDoubles" });
        }
      }
    }
  }, [
    WTN,
    canvasWidth,
    animDuration,
    labels,
    version,
    gameZoneUpper,
    gameZoneLower,
    proPlayer,
    confidence,
    sourceIsPsq,
  ]);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          width: canvasWidth,
          height: canvasWidth,
          backgroundColor: "#ffffff",
        }}
      ></canvas>
    </>
  );
};

export default WTNScale;
